import React from "react";
import ReactDOM from "react-dom";

import { faCommentAltLines } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import EventEmitter from "events";
import { cloneDeep, isEqual } from "lodash";
import { EditorState, Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";
import { commitLocalUpdate, fetchQuery, graphql, type RecordProxy } from "relay-runtime";
import { v4 } from "uuid";

import { showFeatureModal } from "~/billing";
import { Extension } from "~/lib/prose/extension";
import { nGetText } from "~/lib/utils";
import { environment } from "~/models";
import { eventEmitter } from "~/stream/Comments/events";
import { Tooltip, colors as uiColors } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";

import { addAnnotation, COMMENT_ANNOTATION_ORDER, removeAnnotation } from "./blockAnnotation";
import { type Boundaries, getBlockBoundaryState } from "./blockBoundary";
import { getContext } from "./context";

import type { commentsStatsQuery } from "~/__generated__/commentsStatsQuery.graphql";

import styles from "./comments.module.css";

const statsQuery = graphql`
	query commentsStatsQuery($id: ID!) {
		node(id: $id) {
			... on Note {
				id
				comments {
					blockKey
					count
					users {
						id
						fullName
						image
						isExternal
					}
				}
			}
		}
	}
`;

type BlockCommentsInfo = {
	count: number;
	users: {
		id: string;
		image: string;
		fullName: string;
		isExternal: boolean;
	}[];
};

type CommentsData = {
	[block: string]: BlockCommentsInfo;
};
export const CommentsPluginKey = new PluginKey<CommentsState>("block-comments");
const CommentsHighlightPluginKey = new PluginKey("CommentsHighlight");

type InitialCommentsData = NonNullable<NonNullable<commentsStatsQuery["response"]["node"]>["comments"]>;

export type CommentUpdateData = {
	blockKey: string;
} & BlockCommentsInfo;

interface CommentsState {
	commentsData: CommentsData;
	/** The widgets displaying the comment label */
	decorations: DecorationSet;
	/** Position of end of blocks by key */
	blockBoundaries: Readonly<Boundaries>;
}

type CommentsWidgetProps = {
	noteId: string;
	blockKey: string;
	info: BlockCommentsInfo;
	updatesEmitter: EventEmitter;
	context: {
		isMeetingModal?: boolean;
		redirectToNote?: (blockId: string) => Promise<void>;
		viewOnlyAccess?: boolean;
	};
};

function CommentsWidget({ info, noteId, updatesEmitter, blockKey: blockId, context }: CommentsWidgetProps) {
	const [data, setData] = React.useState(info);
	const sendOpenPanel = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			eventEmitter.emit("NoteComments.open", noteId, blockId);
		},
		[blockId, noteId],
	);

	const { isMeetingModal, redirectToNote, viewOnlyAccess } = context;

	const onWidgetClick = React.useCallback(
		(e: React.MouseEvent) => {
			if (viewOnlyAccess) {
				return;
			}

			if (isMeetingModal) {
				redirectToNote?.(blockId);
			} else {
				sendOpenPanel(e);
			}
		},
		[blockId, isMeetingModal, redirectToNote, sendOpenPanel, viewOnlyAccess],
	);

	React.useEffect(() => {
		const listener = (d: null | BlockCommentsInfo) => {
			if (d) {
				setData(d);
			} else {
				setData({ count: 0, users: [] });
			}
		};

		updatesEmitter.addListener("data", listener);
		return () => {
			updatesEmitter.removeListener("data", listener);
		};
	}, [setData, updatesEmitter]);

	const firstAttendees = data.users.slice(0, 4);
	const othersAttendees = data.users.slice(4);
	const othersLength = othersAttendees.length;

	const renderAttendee = (user: BlockCommentsInfo["users"][0]) => {
		const attendee = {
			...user,
			user,
		};

		return (
			<div className={styles.attendee} key={`user-avatar-${user.id}`}>
				<Avatar user={attendee} width="16px" link={false} useTitle={false} hideExternalIcon={true} />
			</div>
		);
	};

	const othersAvatar = (
		<Avatar
			width="16px"
			link={false}
			useTitle={false}
			uiAvatarOptions={{ background: uiColors.grey[200].substring(1), color: uiColors.grey[600].substring(1) }}
			initials={othersLength}
			hideExternalIcon={true}
		/>
	);

	if (!data.count) {
		return null;
	}

	return (
		<Tooltip content="View in note" placement="top" disabled={!isMeetingModal || viewOnlyAccess}>
			<button
				className={cx(styles.commentsWrapper, { [styles.disabledButton]: viewOnlyAccess })}
				onClick={onWidgetClick}
				disabled={viewOnlyAccess}
			>
				<div className={styles.attendeesWrapper}>
					{othersLength > 0 && (othersLength === 1 ? renderAttendee(othersAttendees[0]) : othersAvatar)}
					{firstAttendees.map(attendee => renderAttendee(attendee))}
				</div>

				<div
					className={styles.commentsLabel}
				>{`${data.count} ${nGetText(data.count || 0, "comment", "comments")}`}</div>
			</button>
		</Tooltip>
	);
}

const openComments = (noteId: string) => {
	return (state: EditorState): boolean => {
		const context = getContext(state);
		const hasAccessToComments = context?.billing?.hasAccessToComments;
		if (!hasAccessToComments) {
			showFeatureModal("comments");
			return true;
		}
		const { from, to } = state.selection;
		state.doc.nodesBetween(from, to, node => {
			const blockId = node.attrs.blockId;
			if (blockId) {
				eventEmitter.emit("NoteComments.open", noteId, blockId);
				return;
			}
		});

		return true;
	};
};

export default class Comments extends Extension {
	name = "block-comments";
	noteId: string;
	initialData: CommentsData;

	commentWidgetNodes = new Map<string, Element>();
	blockDataEmitters = new Map<string, EventEmitter>();

	constructor({ noteId, initialData }: { noteId: string; initialData: InitialCommentsData }) {
		super();

		this.noteId = noteId;
		this.initialData = {};

		for (const { users, blockKey, count } of initialData) {
			this.initialData[blockKey] = { count, users: [...users] };
		}
	}

	menuEntry() {
		return {
			icon: faCommentAltLines,
			title: "Comment",
			run: () => openComments(this.noteId),
			section: "mostUsedAndSocial" as const,
			priority: 1,
		};
	}

	dispatchCommentsUpdate(updateData: CommentUpdateData) {
		this.editorView?.dispatch(this.editorView.state.tr.setMeta(CommentsPluginKey, updateData));
	}

	applyUpdateData = (oldData: CommentsData, update: CommentUpdateData): CommentsData => {
		const { blockKey, count, users } = update;
		const newData = cloneDeep(oldData);

		if (count) {
			newData[blockKey] = { count, users };
		} else {
			delete newData[blockKey];
		}

		this.blockDataEmitters.get(blockKey)?.emit("data", newData[blockKey]);
		this.updateRelayStorage(newData);

		return newData;
	};

	refreshCommentsData = (dataFromQuery: InitialCommentsData) => {
		if (!dataFromQuery) return;
		const view = this.editorView;
		if (!view) return;
		const transformed = dataFromQuery.reduce((acc, item) => {
			return { ...acc, [item.blockKey]: { users: [...item.users], count: item.count } };
		}, {} as CommentsData);
		view.dispatch(view.state.tr.setMeta("commentsRefetched", transformed));
		this.blockDataEmitters.forEach((emitter, k) => {
			const data = transformed[k];
			if (data) {
				emitter.emit("data", data);
			}
		});
	};

	refetchCommentsData = () => {
		const view = this.editorView;
		if (!view) return;
		const state = view.state;
		const noteId = getContext(state)?.noteId;
		if (!noteId) return;
		fetchQuery<commentsStatsQuery>(environment, statsQuery, { id: noteId })
			.toPromise()
			.then(node => {
				const data = node?.node?.comments;
				if (!data) return;
				this.refreshCommentsData(data);
			});
	};

	updateRelayStorage = (data: CommentsData) => {
		const editorView = this.editorView;
		if (!editorView) return;
		const noteId = getContext(editorView.state)?.noteId;
		if (!noteId) return;
		commitLocalUpdate(environment, store => {
			const note = store.get(noteId);
			const existingCommentRecords = note?.getLinkedRecords("comments");
			const updatedKeys = new Set();
			function updateCommentRecord(commentRecord: RecordProxy) {
				const blockKey = commentRecord.getValue("blockKey") as string;
				const blockCommentData = data[blockKey];
				if (blockCommentData) {
					// @ts-ignore
					commentRecord.setValue(blockCommentData.count, "count");
					const newUserRecords = blockCommentData.users.map(u => {
						const existingRecord = store.get(u.id);
						if (existingRecord) {
							return existingRecord;
						}
						const record = store.create(u.id, "User");
						record.setValue(u.id, "id");
						record.setValue(u.fullName, "fullName");
						record.setValue(u.image, "image");
						return record;
					});
					commentRecord.setLinkedRecords(newUserRecords, "users");
				} else {
					commentRecord.setValue(0, "count");
					commentRecord.setLinkedRecords([], "users");
				}
			}
			existingCommentRecords?.forEach(commentRecord => {
				const blockKey = commentRecord.getValue("blockKey") as string;
				updatedKeys.add(blockKey);
				updateCommentRecord(commentRecord);
			});
			const newRecords: RecordProxy[] = [];
			Object.keys(data).forEach(key => {
				if (updatedKeys.has(key)) return;
				const record = store.create(v4(), "NoteCommentsData");
				record.setValue(key, "blockKey");
				updateCommentRecord(record);
				newRecords.push(record);
			});
			note?.setLinkedRecords([...(existingCommentRecords || []), ...newRecords], "comments");
		});
	};

	updateWidgets = (newState: EditorState, currentPluginState: CommentsState, commentsDataChanged = false) => {
		const editorView = this.editorView;
		if (!editorView) return;
		const blockBoundaryState = getBlockBoundaryState(newState);
		if (blockBoundaryState === undefined) return;
		const { blockBoundaries: newBlockBoundaries } = blockBoundaryState;

		const removedBlockKeys = new Set(
			Object.keys(currentPluginState.blockBoundaries).filter(k => !(k in newBlockBoundaries)),
		);
		const blockKeysWithCommentsRemoved = new Set(
			[...this.commentWidgetNodes.keys()].filter(k => !(k in currentPluginState.commentsData)),
		);
		removedBlockKeys.forEach(blockKey => {
			let dom = this.commentWidgetNodes.get(blockKey);
			if (dom) {
				ReactDOM.unmountComponentAtNode(dom);
				removeAnnotation(editorView, blockKey, dom);
			}
			this.commentWidgetNodes.delete(blockKey);
			this.blockDataEmitters.delete(blockKey);
		});
		blockKeysWithCommentsRemoved.forEach(k => {
			let dom = this.commentWidgetNodes.get(k);
			if (dom) {
				ReactDOM.unmountComponentAtNode(dom);
				removeAnnotation(editorView, k, dom);
				dom.remove();
			}
			this.commentWidgetNodes.delete(k);
			this.blockDataEmitters.delete(k);
		});
		if (!commentsDataChanged && isEqual(newBlockBoundaries, currentPluginState.blockBoundaries)) return;

		Object.entries(currentPluginState.commentsData).forEach(([k, v]) => {
			if (!(k in newBlockBoundaries)) return;

			let dom = this.commentWidgetNodes.get(k);
			let context = getContext(newState);

			if (!dom) {
				const div = document.createElement("div");
				div.contentEditable = "false";
				dom = div;
				this.commentWidgetNodes.set(k, dom);
				let emitter = this.blockDataEmitters.get(k);
				if (!emitter) {
					emitter = new EventEmitter();
					this.blockDataEmitters.set(k, emitter);
				}
				ReactDOM.render(
					<CommentsWidget noteId={this.noteId} blockKey={k} info={v} updatesEmitter={emitter} context={context} />,
					div,
				);
				addAnnotation(editorView, k, div, COMMENT_ANNOTATION_ORDER);
			}
		});
	};

	get plugins() {
		const ext = this;
		return [
			new Plugin({
				key: CommentsHighlightPluginKey,
				props: {
					decorations: state => {
						const highlightBlockKey = CommentsHighlightPluginKey.getState(state) as string | null;
						if (!highlightBlockKey) return DecorationSet.create(state.doc, []);
						const blockBoundaryState = getBlockBoundaryState(state);
						if (blockBoundaryState === undefined) return;
						const { blockBoundaries } = blockBoundaryState;
						if (!(highlightBlockKey in blockBoundaries)) return DecorationSet.create(state.doc, []);
						const [start, end] = blockBoundaries[highlightBlockKey];
						return DecorationSet.create(state.doc, [Decoration.node(start, end, { class: styles.commentsActive })]);
					},
				},
				state: {
					init: (_config, _state: EditorState) => {
						return null;
					},
					apply: (tr, value) => {
						const newValue = tr.getMeta(CommentsHighlightPluginKey);
						if (typeof newValue === "undefined") {
							return value;
						}
						return newValue;
					},
				},
			}),
			new Plugin<CommentsState>({
				key: CommentsPluginKey,
				props: {
					decorations(state) {
						return CommentsPluginKey.getState(state)?.decorations;
					},
				},
				state: {
					init(_config, state: EditorState) {
						const value = {
							commentsData: ext.initialData,
							decorations: DecorationSet.create(state.doc, []),
							blockBoundaries: {},
						};
						ext.updateWidgets(state, value);
						return value;
					},
					apply(tr, value, _oldState, newState) {
						let dataChanged = false;

						if (tr.getMeta(CommentsPluginKey)) {
							const newData: CommentUpdateData = tr.getMeta(CommentsPluginKey);
							value.commentsData = ext.applyUpdateData(value.commentsData, newData);
							dataChanged = true;
						} else if (tr.getMeta("commentsRefetched")) {
							dataChanged = true;
							value.commentsData = tr.getMeta("commentsRefetched");
						}

						ext.updateWidgets(newState, value, dataChanged);
						return value;
					},
				},
			}),
		];
	}

	onCommentsOpen = (noteId: string, blockId: string) => {
		if (noteId !== this.noteId) return;
		this.editorView?.dispatch(this.editorView.state.tr.setMeta(CommentsHighlightPluginKey, blockId));
	};
	onCommentsClose = (noteId: string, _blockId: string) => {
		if (noteId !== this.noteId) return;
		this.editorView?.dispatch(this.editorView.state.tr.setMeta(CommentsHighlightPluginKey, null));
	};

	onDataUpdate = (id: string, data: InitialCommentsData) => {
		if (id !== this.noteId) return;
		this.refreshCommentsData(data);
	};

	init = () => {
		eventEmitter.addListener("NoteComments.open", this.onCommentsOpen);
		eventEmitter.addListener("NoteComments.close", this.onCommentsClose);
		eventEmitter.addListener("NoteComments.dataUpdate", this.onDataUpdate);
	};

	destroy = () => {
		eventEmitter.removeListener("NoteComments.open", this.onCommentsOpen);
		eventEmitter.removeListener("NoteComments.close", this.onCommentsClose);
		eventEmitter.removeListener("NoteComments.dataUpdate", this.onDataUpdate);

		this.commentWidgetNodes.forEach(v => {
			ReactDOM.unmountComponentAtNode(v);
			v.remove();
		});
	};
}
